import { default as _log } from '@dw/loglevel';
import Query from './query.js';

//lodash
import forEach from "lodash-es/forEach";
import isEqual from "lodash-es/isEqual";
import keyBy from "lodash-es/keyBy";

export default class QueryFind extends Query {

  constructor({ couchdb, database, queryId, dispatch, selector, useIndex, limit, skip, sort, localOnly, once }) {
    super({ couchdb, database, queryId, localOnly, once, dispatch });

    this.selector = selector;
    this.useIndex = useIndex;
    this.limit = limit;
    this.skip = skip;
    this.sort = sort;
    this._onLocalDbChanges = this._onLocalDbChanges.bind(this);
    // logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.database.dbName}.QueryFind.${this.queryId}`;
    //set logger
    this.log = _log.getLogger(`dreamdb.find.${this.couchdb.name}.${this.database.dbName}.${this.queryId}`);
    this._dispatchQuery();
    this._startQuery();
  }

  /**
   * Returns serialized format of the query. e.g. `{id:"", type:"BY_ID", docId: ""}`
   */
  get query() {
    return {
      id: this.queryId,
      type: 'FIND',
      selector: this.selector,
      useIndex: this.useIndex,
      limit: this.limit,
      skip: this.skip,
      sort: this.sort,
      localOnly: this.localOnly,
      once: this.once
    };
  }

  /**
   * This method starts read query in local database as well as on remote database.
   */
  async _startQuery() {
    this._setMetadata({ lastQueryTime: new Date().getTime() });

    if (this.once) {
      this._readQueryOnce();
      return;
    }

    this._localQuery();
    if (!this.localOnly) {
      this._remoteQuery();
    }
  }

  /**
   * It performs first query on remote database and after performs on local database.If once=true, then it will 
   * perform query only on local database.
   */
  async _readQueryOnce() {
    //Perform only on local database
    if (this.localOnly) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true });
      return;
    }

    //Perform on both remote database and local database
    let response = await this.database.remoteDb.queryFind(this.query);
    this._startSchedulerForWarning();

    if (response.warning) {
      this.log.warn(`${this.logPrefix}._readQueryOnce(): warning=`, response.warning);
    }

    if (response.success) {
      await this._performLocalQuery();
      this._resolveVal(this.value);
      this._setMetadata({ localInSync: true, inSync: true });
      return;
    }

    this.log.error(`${this.logPrefix}._readQueryOnce(): once query operation failed::`, response.error);
    this._rejectVal(response.error);
    this._setMetadata({ localInSync: true, inSync: true, error: response.error });
  }

  /**
   * Runs find query on the local database.
   * 
   * @returns {Promise} It's resolved to the Array of Documents matching the query.
   */
  async _findQuery() {
    let localDatabase = await this.database.localDb.localDB;

    return localDatabase.find({
      selector: this.selector,
      limit: this.limit,
      skip: this.skip,
      sort: this.sort,
      use_index: this.useIndex
    });
  }

  /**
   * It fetches the document from the local db and set value
   */
  async _performLocalQuery() {
    await this._waitForDesignDoc(this.useIndex);
    let response = await this._findQuery();
    this.log.debug(`${this.logPrefix}._performLocalQuery(): documents are read from localDb ::`, response);

    let value = response.docs;
    if (!isEqual(this.value, value)) {
      this._setValue(value);
    }
  }

  /**
   * Waits till required design-doc exists in the local database.
   * Runs query, and dispatches `snapshot` event if at least 1 document is found in the query result.
   */
  async _localQuery() {
    try {
      await this._waitForDesignDoc(this.useIndex);

      let result = await this._findQuery();

      this._watchLocalDbChanges();

      if (!result.docs.length) {
        this.log.debug(`${this.logPrefix}._localQuery(): documents not found in local database.`);
        if (this.localOnly) {
          this._setValue([]);
          this._resolveVal([]);
        }
        this._setMetadata({ localInSync: true });
        return;
      }

      this.log.info(`${this.logPrefix}._localQuery(): local query is completed`);
      this._setValue(result.docs);
      this._setMetadata({ localInSync: true });

      if (this.localOnly) {
        this._resolveVal(this.value);
      }
    } catch (err) {
      this.log.error(`${this.logPrefix}._localQuery(): local Query operation failed`, err);
      this._rejectVal(err);
    }
  }

  /**
   * Performs remote-query.
   * On response, if the value isn't yet known, dispatches `snapshot` as empty array; and 
   * dispatches `metadata` event.
   */
  async _remoteQuery() {
    let response = await this.database.remoteDb.queryFind(this.query);
    this._waitingForDDoc && this._startSchedulerForWarning();

    if (response.warning) {
      this.log.warn(`${this.logPrefix}._remoteQuery(): warning=`, response.warning);
    }

    if (response.success) {
      if (this.value === undefined) {
        // When documents not found from local database then we re-runs the query in local database after remote query
        // is completed to prevents extra `null` value snapshot.
        await this._performLocalQuery();
      }

      this._setMetadata({ inSync: true });

      //Resolve val() after server sync is completed.
      this._resolveVal(this.value);
      return;
    }

    this._rejectVal(response.error);
    this.log.error(`${this.logPrefix}._remoteQuery(): remote Query operation failed::`, response.error);
    this._setMetadata({ inSync: true, error: response.error });
  }

  /**
   * An intenal method used by update method.
   * It will update remote query.
   */
  async _updateRemoteQuery() {
    let response = await this.database.remoteDb.updateQueryFind(this.query);

    if (response.success) {
      this._setMetadata({ inSync: true });
      return;
    }

    this.log.error(`${this.logPrefix}._updateRemoteQuery(): update remote Query operation failed::`, response.error);
    this._setMetadata({ inSync: true, error: response.error });
  }

  /**
   * It will check document is matches with current query criteria or not
   *  
   * @param {Object} doc - changed document
   * @param {Boolean} true, If matches createria. false otherwise.
   */
  _isMatchQueryCriteria(doc) {
    return window.matchesSelector(doc, this.selector);
  }

  /**
   * This method called on localDb `changes` event.
   * It updates value only if changedDoc matched with criteria
   * @param {Array} docs Array of changed documents in local database
   */
  _onLocalDbChanges(docs) {
    //if any of the updated document is either in the current query value or document's new
    //value matches query criteria then refresh query 

    let curValue = keyBy(this.value, '_id');

    forEach(docs, (doc) => {
      if (this._isMatchQueryCriteria(doc.doc) || curValue[doc.id]) {
        this._refresh(docs);
        return false;
      }
    });
  }

  /**
   * re-runs query on the local database, and if any update is found in the `value` dispatches
   * `snapshot` event.
   */
  async _refresh(changedDocs) {
    let curResult = [...this.value || []];
    let result = await this._findQuery();
    this.log.info(`${this.logPrefix}._refresh(): query refresh completed`);

    if (!result.docs.length) {
      this._setValue([]);
      this._updateChangedDocs(curResult, [], changedDocs);
      return;
    }

    this._setValue(result.docs);
    this._updateChangedDocs(curResult, result.docs, changedDocs);
  }

  /**
   * Invokes `super._setvalue(docs)` only when value is really updated.,
   * Value updated means, any of the following is true:
   * - One of the document is added in the Array
   * - One of the document is updated
   * - One of the document is removed from the Array
   * @param {*} docs New value of the query.
   */
  _setValue(docs) {

    if (!docs.length) {
      super._setValue([]);
      return;
    }

    let newValue = [];
    let curValue = this.value || [];

    docs.forEach(newDoc => {
      let oldDoc = curValue.find((doc) => {
        return doc._id === newDoc._id && doc._rev === newDoc._rev;
      });

      newValue.push(oldDoc || newDoc);
    });

    if (isEqual(curValue, newValue)) {
      return;
    }

    super._setValue(newValue);
  }

  /**
   * This method will used for update current query's criteria except once and localOnly
   * It will dispatch updata query redux action, refresh query and then update remote query
   * It will throw error if method is called for one time (once=true) query.
   * @param {Object} It contains new value of selector and useIndex.  
   */
  update({ queryId, selector, useIndex, limit, skip, sort }) {
    if (this.once) {
      throw `Can't update once query`;
    }

    if (queryId && this.queryId !== queryId) {
      this.log.warn(`${this.logPrefix}.update(): queryId can't be changed.`);
    }

    this.selector = selector || this.selector;
    this.useIndex = useIndex || this.useIndex;
    this.limit = limit || this.limit;
    this.skip = skip || this.skip;
    this.sort = sort || this.sort;
    this._dispatchQueryUpdate();
    this._setMetadata({ lastQueryTime: new Date().getTime(), localInSync: false, inSync: false });
    this._refresh().then(() => {
      this._setMetadata({ localInSync: true });
    });

    //Skip to update remote query, if localOnly=true
    if (this.localOnly) {
      return;
    }

    this._updateRemoteQuery();
  }

}
